// global-state.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export type GlobalMenuActionType = 'upload' | 'new-file' | 'new-from-template';

export interface IGlobalMenuActionRequest {
  action: GlobalMenuActionType;
  item?: any;
  category?: number;
  event?: any;
}

@Injectable({
  providedIn: 'root'   // makes it global automatically
})
export class GlobalStateService {

  private isPreviewOpen = new BehaviorSubject<boolean>(false);
  private isBizEntityCreated = new BehaviorSubject<boolean>(false);
  private createdFileData = new BehaviorSubject<{ file: any; category: number } | null>(null);
  private selectedTemplateData = new BehaviorSubject<{ templateId: string; fileUrl: string; templateName: string } | null>(null);
  private isBizEntityUpdated = new BehaviorSubject<{ updated: boolean; entity?: any }>({ updated: false });
  private isBizEntityDeleted = new BehaviorSubject<{ deleted: boolean; entity?: any }>({ deleted: false });
  private isRenameClicked = new BehaviorSubject<boolean>(false);
  private isPropertiesOpenClicked = new BehaviorSubject<boolean>(false);
  private isOpenEntityClicked = new BehaviorSubject<{ open: boolean; entity?: any }>({ open: false });
  private selectedBizEntityId = new BehaviorSubject<string | null>(null);
  private updateBizEntity = new BehaviorSubject<string | null>(null);
  private isLibraryOpen = new BehaviorSubject<boolean>(false);
  private isEditEntityOpen = new BehaviorSubject<boolean>(false);
  private isFolderOpen = new BehaviorSubject<boolean>(false);
  private menuActionRequested = new Subject<IGlobalMenuActionRequest>();
  private saveRequested = new BehaviorSubject<{ timestamp: number; entityId?: string; source?: string } | null>(null);
  isPreviewOpen$ = this.isPreviewOpen.asObservable();
  selectedBizEntityId$ = this.selectedBizEntityId.asObservable();
  isBizEntityCreated$ = this.isBizEntityCreated.asObservable();
  createdFileData$ = this.createdFileData.asObservable();
  selectedTemplateData$ = this.selectedTemplateData.asObservable();
  isBizEntityUpdated$ = this.isBizEntityUpdated.asObservable();
  isBizEntityDeleted$ = this.isBizEntityDeleted.asObservable();
  isRenameClicked$ = this.isRenameClicked.asObservable();
  isPropertiesOpenClicked$ = this.isPropertiesOpenClicked.asObservable();
  isOpenEntityClicked$ = this.isOpenEntityClicked.asObservable();
  updateBizEntity$ = this.updateBizEntity.asObservable();
  isLibraryOpen$ = this.isLibraryOpen.asObservable();
  isEditEntityOpen$ = this.isEditEntityOpen.asObservable();
  isFolderOpen$ = this.isFolderOpen.asObservable();
  menuActionRequested$ = this.menuActionRequested.asObservable();
  saveRequested$ = this.saveRequested.asObservable();
  setIsPreviewOpen(status: boolean) {
    this.isPreviewOpen.next(status);
  }

  setSelectedBizEntityId(id: string | null) {
    this.selectedBizEntityId.next(id);
  }

  setUpdateBizEntity(id: string | null) {
    this.updateBizEntity.next(id);
  }

  setBizEntityCreated(status: boolean) {
    this.isBizEntityCreated.next(status);
  }

  setCreatedFileData(file: any, category: number) {
    this.createdFileData.next({ file, category });
  }

  clearCreatedFileData() {
    this.createdFileData.next(null);
  }

  setSelectedTemplateData(templateId: string, fileUrl: string, templateName: string) {
    this.selectedTemplateData.next({ templateId, fileUrl, templateName });
  }

  clearSelectedTemplateData() {
    this.selectedTemplateData.next(null);
  }

  setBizEntityUpdated(status: boolean, entity?: any) {
    this.isBizEntityUpdated.next({ updated: status, entity });
  }

  setBizEntityDeleted(status: boolean, entity?: any) {
    this.isBizEntityDeleted.next({ deleted: status, entity });
  }

  setRenameClicked(status: boolean) {
    this.isRenameClicked.next(status);
  }
  setPropertiesOpenClicked(status: boolean) {
    this.isPropertiesOpenClicked.next(status);
  }

  setOpenEntityClicked(status: boolean, entity?: any) {
    this.isOpenEntityClicked.next({ open: status, entity });
  }

  setIsLibraryOpen(status: boolean) {
    this.isLibraryOpen.next(status);
  }

    setIsEditEntityOpen(status: boolean) {
    this.isEditEntityOpen.next(status);
  }

  setIsFolderOpen(status: boolean) {
    this.isFolderOpen.next(status);
  }

  requestMenuAction(action: GlobalMenuActionType, item?: any, category?: number, event?: any) {
    this.menuActionRequested.next({ action, item, category, event });
  }

  requestSave(entityId?: string, source: string = 'unknown') {
    this.saveRequested.next({
      timestamp: Date.now(),
      entityId,
      source,
    });
  }

  requestSaveWhiteboard(entityId?: string, source: string = 'unknown') {
    this.requestSave(entityId, source);
  }

  // Query parameter persistence methods
  private readonly QUERY_PARAMS_KEY = 'hayagriva_query_params';

  /**
   * Store query parameters in localStorage
   */
  storeQueryParameters(params: { isOpen?: string; id?: string;[key: string]: any }): void {
    try {
      // Only store if we have relevant parameters
      if (params.isOpen || params.id || params['isNew']) {
        const filteredParams = {
          isOpen: params.isOpen,
          id: params.id,
          // Store any other query parameters that might be relevant
          parentId: params['parentId'],
          typeId: params['typeId'],
          isNew: params['isNew'],
          timestamp: Date.now() // Add timestamp to prevent stale data
        };

        localStorage.setItem(this.QUERY_PARAMS_KEY, JSON.stringify(filteredParams));
      }
    } catch (error) {
    }
  }

  /**
   * Retrieve stored query parameters from localStorage
   */
  getStoredQueryParameters(): { isOpen?: string; id?: string;[key: string]: any } | null {
    try {
      const stored = localStorage.getItem(this.QUERY_PARAMS_KEY);
      if (stored) {
        const params = JSON.parse(stored);

        // Check if the stored params are not too old (24 hours max)
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        if (params.timestamp && (Date.now() - params.timestamp) < maxAge) {
          return params;
        } else {
          // Remove stale data
          this.clearStoredQueryParameters();
        }
      }
    } catch (error) {
    }
    return null;
  }

  /**
   * Clear stored query parameters from localStorage
   */
  clearStoredQueryParameters(): void {
    try {
      localStorage.removeItem(this.QUERY_PARAMS_KEY);
    } catch (error) {
    }
  }

  /**
   * Check if we have stored query parameters
   */
  hasStoredQueryParameters(): boolean {
    return this.getStoredQueryParameters() !== null;
  }
}
